import type { JSX } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Mentions, message, Popconfirm } from 'antd';
import { CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';

import { wikiApi, type WikiCommentDto } from '../lib/wikiApi';
import { authApi } from '../lib/authApi';

/**
 * Тред комментариев под статьёй (Q8/Q46 — УК пишет, партнёры комментируют,
 * видны всем читателям). Top-level + одноуровневые ответы. Комментировать
 * могут только CRM-юзеры; сотрудники iiko видят, но форму не показываем.
 */
export function CommentsThread({ slug }: { slug: string }): JSX.Element {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['wiki-auth', 'me'],
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 60_000,
  });
  const canComment = meQuery.data?.type === 'crm_user';

  const { data: comments, isLoading } = useQuery({
    queryKey: ['wiki', 'comments', slug],
    queryFn: () => wikiApi.listComments(slug),
    enabled: !!slug,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['wiki', 'comments', slug] });

  const addMut = useMutation({
    mutationFn: ({ bodyMd, parentId }: { bodyMd: string; parentId?: string }) =>
      wikiApi.addComment(slug, bodyMd, parentId),
    onSuccess: invalidate,
    onError: (e: Error) => message.error(e.message),
  });

  const total = countComments(comments ?? []);

  return (
    <section className="fk-comments" style={{ marginTop: 20, maxWidth: 880, background: 'var(--fk-surface)', border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-card)', padding: '18px 20px' }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px', color: 'var(--fk-text)' }}>
        Комментарии {total > 0 && <span style={{ color: 'var(--fk-text-muted)', fontWeight: 500 }}>· {total}</span>}
      </h3>

      {canComment && (
        <CommentForm
          placeholder="Написать комментарий…"
          loading={addMut.isPending}
          onSubmit={(bodyMd) => addMut.mutate({ bodyMd })}
        />
      )}

      {isLoading && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>загрузка…</div>}
      {!isLoading && (comments?.length ?? 0) === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 12 }}>
          Пока нет комментариев. {canComment ? 'Будьте первым.' : ''}
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {(comments ?? []).map((c) => (
          <CommentNode key={c.id} comment={c} slug={slug} canReply={canComment} depth={0} />
        ))}
      </div>
    </section>
  );
}

function countComments(list: WikiCommentDto[]): number {
  return list.reduce((acc, c) => acc + 1 + countComments(c.replies), 0);
}

function CommentNode({
  comment,
  slug,
  canReply,
  depth,
}: {
  comment: WikiCommentDto;
  slug: string;
  canReply: boolean;
  depth: number;
}): JSX.Element {
  const qc = useQueryClient();
  const [replying, setReplying] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['wiki', 'comments', slug] });

  const replyMut = useMutation({
    mutationFn: (bodyMd: string) => wikiApi.addComment(slug, bodyMd, comment.id),
    onSuccess: () => { setReplying(false); invalidate(); },
    onError: (e: Error) => message.error(e.message),
  });
  const resolveMut = useMutation({
    mutationFn: () => wikiApi.resolveComment(comment.id, !comment.isResolved),
    onSuccess: invalidate,
  });
  const delMut = useMutation({
    mutationFn: () => wikiApi.deleteComment(comment.id),
    onSuccess: invalidate,
  });

  return (
    <div style={{ marginLeft: depth > 0 ? 36 : 0 }}>
      <div style={{ display: 'flex', gap: 10 }}>
        <Avatar size={32} src={comment.author.avatarUrl ?? undefined} style={{ background: 'var(--fk-indigo)', flexShrink: 0, fontSize: 11, fontWeight: 700 }}>
          {comment.author.name.slice(0, 1).toUpperCase()}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{comment.author.name}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {new Date(comment.createdAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
            {comment.isResolved && (
              <span style={{ fontSize: 11, color: 'var(--brasol-success)', fontWeight: 600 }}>
                <CheckCircleOutlined /> решено
              </span>
            )}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', marginTop: 2 }}>
            {renderWithMentions(comment.bodyMd)}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            {canReply && depth === 0 && (
              <button type="button" onClick={() => setReplying((v) => !v)} style={linkBtn}>
                Ответить
              </button>
            )}
            {comment.canManage && (
              <>
                <button type="button" onClick={() => resolveMut.mutate()} style={linkBtn}>
                  {comment.isResolved ? 'Вернуть' : 'Решено'}
                </button>
                <Popconfirm title="Удалить комментарий?" onConfirm={() => delMut.mutate()} okText="Да" cancelText="Нет">
                  <button type="button" style={{ ...linkBtn, color: 'var(--brasol-danger, #d4380d)' }}>
                    <DeleteOutlined />
                  </button>
                </Popconfirm>
              </>
            )}
          </div>
          {replying && (
            <div style={{ marginTop: 8 }}>
              <CommentForm
                placeholder="Ваш ответ…"
                loading={replyMut.isPending}
                onSubmit={(b) => replyMut.mutate(b)}
                compact
              />
            </div>
          )}
        </div>
      </div>
      {comment.replies.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {comment.replies.map((r) => (
            <CommentNode key={r.id} comment={r} slug={slug} canReply={canReply} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function CommentForm({
  placeholder,
  loading,
  onSubmit,
  compact,
}: {
  placeholder: string;
  loading: boolean;
  onSubmit: (bodyMd: string) => void;
  compact?: boolean;
}): JSX.Element {
  const [value, setValue] = useState('');
  // @упоминания (фаза 1.4): подсказка по «@», выбранные имена → id; при
  // отправке `@Имя Фамилия` превращается в `@[Имя Фамилия](user:<id>)`.
  const [mentionQuery, setMentionQuery] = useState('');
  const picked = useRef(new Map<string, string>());
  const suggest = useQuery({
    queryKey: ['wiki', 'users-suggest', mentionQuery],
    queryFn: () => wikiApi.suggestUsers(mentionQuery),
    staleTime: 60_000,
  });
  const options = useMemo(
    () => (suggest.data ?? []).map((u) => ({ value: u.fullName, label: u.fullName, key: u.id })),
    [suggest.data],
  );
  const submit = () => {
    let v = value.trim();
    if (!v) return;
    for (const [name, id] of picked.current) {
      v = v.split(`@${name}`).join(`@[${name}](user:${id})`);
    }
    onSubmit(v);
    setValue('');
    picked.current.clear();
  };
  return (
    <div style={{ marginBottom: compact ? 0 : 16 }}>
      <Mentions
        value={value}
        onChange={setValue}
        placeholder={placeholder}
        autoSize={{ minRows: compact ? 1 : 2, maxRows: 6 }}
        maxLength={4000}
        prefix="@"
        split=" "
        onSearch={setMentionQuery}
        onSelect={(opt) => {
          const u = (suggest.data ?? []).find((x) => x.fullName === opt.value);
          if (u) picked.current.set(u.fullName, u.id);
        }}
        options={options}
        filterOption={false}
        loading={suggest.isFetching}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
        }}
      />
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button type="primary" size="small" loading={loading} disabled={!value.trim()} onClick={submit}
          >
          Отправить
        </Button>
      </div>
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--brasol-accent)',
  fontSize: 12,
  padding: 0,
  fontWeight: 600,
};

/** `@[Имя](user:id)` → чип упоминания; остальной текст как есть. */
function renderWithMentions(text: string): React.ReactNode {
  const re = /@\[([^\]]+)\]\(user:[0-9a-fA-F-]{36}\)/g;
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <span key={`m${i++}`} style={{ background: 'var(--fk-orange-soft)', color: 'var(--fk-orange)', borderRadius: 4, padding: '0 5px', fontWeight: 600 }}>
        @{m[1]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length > 0 ? out : text;
}
